import React from "react";
import { MapPin, Phone, Clock, Mail, ExternalLink } from "lucide-react";
import { CONTACT } from "@/lib/testIds";
import { useRestaurant } from "@/lib/brandingStore";
import { Seo } from "@/lib/seo";

const Contact = () => {
  const r = useRestaurant();
  const phoneTel = r.phone_tel || `tel:${(r.phone_display || "").replace(/\s+/g, "")}`;
  const mapsUrl =
    r.maps_url ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${r.address_street} ${r.address_zip} ${r.address_city}`
    )}`;

  return (
    <div
      data-testid={CONTACT.root}
      className="mx-auto max-w-6xl px-4 sm:px-6 pt-12 sm:pt-16 pb-24"
    >
      <Seo
        title="Contact LE 11 - Adresse, horaires et téléphone à Villiers-le-Bel"
        description={`Adresse, horaires et téléphone du restaurant LE 11 à ${r.address_city}. Pizza personnalisée, burgers paratha, cheese naan et sandwich merguez à emporter.`}
      />

      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.22em] text-[#C9A875] font-bold">
          Nous trouver
        </div>
        <h1 className="font-street mt-3 text-5xl sm:text-6xl tracking-wide leading-[1.02]">
          Passez nous voir
          <br />
          <span className="text-[#C9A875]">au comptoir.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#A1A1AA]">
          Retrouvez-nous au comptoir, ou appelez-nous pour toute question
          sur la carte.
        </p>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5 space-y-4">
          <InfoCard
            icon={<MapPin size={18} />}
            title="Adresse"
            testId={CONTACT.address}
            value={r.address_street}
            sub={`${r.address_zip} ${r.address_city}`}
            action={
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-maps-link"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#F5EFE6]/20 bg-[#F5EFE6]/[0.04] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-street text-[#F5EFE6] hover:border-[#F5EFE6]/35 hover:bg-[#F5EFE6]/[0.08] transition-colors"
              >
                Itinéraire <ExternalLink size={11} />
              </a>
            }
          />
          <InfoCard
            icon={<Phone size={18} />}
            title="Téléphone"
            testId={CONTACT.phone}
            value={
              <a
                href={phoneTel}
                data-testid="contact-phone-link"
                className="hover:text-[#F5EFE6] transition-colors"
              >
                {r.phone_display}
              </a>
            }
            sub="Appels et réservations"
          />
          <InfoCard
            icon={<Mail size={18} />}
            title="Email"
            value={
              <a
                href={`mailto:${r.email}`}
                data-testid="contact-email-link"
                className="hover:text-[#F5EFE6] transition-colors break-all"
              >
                {r.email}
              </a>
            }
            sub="Réponse sous 48h"
          />
        </div>

        <div
          data-testid={CONTACT.mapPlaceholder}
          className="lg:col-span-7 overflow-hidden rounded-2xl border border-[#262626] bg-[#171717] le11-shadow-card"
        >
          {/* Photo du restaurant — fallback "11" si pas d'URL */}
          {r.photo_url ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="relative block h-64 sm:h-80 overflow-hidden group"
              data-testid="contact-restaurant-photo"
            >
              <img
                src={r.photo_url}
                alt={`${r.name} — ${r.address_city}`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-xs uppercase tracking-[0.22em] font-street text-white">
                <span>{r.address_city}</span>
                <span className="inline-flex items-center gap-1 text-[#FFC447]">
                  Itinéraire <ExternalLink size={11} />
                </span>
              </div>
            </a>
          ) : (
            <div
              className="relative h-64 sm:h-80 bg-[#0F0F0F]"
              data-testid="contact-restaurant-fallback"
            >
              <div className="absolute inset-0 flex items-center justify-center text-center px-6">
                <div>
                  <div className="font-display text-7xl text-[#C9A875] drop-shadow-[0_3px_0_rgba(0,0,0,0.6)]">
                    11
                  </div>
                  <div className="mt-2 text-sm text-[#A1A1AA]">
                    Retrouvez-nous en boutique
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_18px,rgba(255,149,0,0.05)_18px,rgba(255,149,0,0.05)_36px)] pointer-events-none" />
            </div>
          )}

          <div data-testid={CONTACT.hours} className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A1A1AA]">
              <Clock size={14} />
              Horaires d'ouverture
            </div>
            <ul className="mt-4 divide-y divide-[#262626]">
              {(r.hours || []).map((d) => (
                <li
                  key={d.day}
                  className="flex items-center justify-between py-2.5 text-sm"
                  data-testid={`contact-hours-${d.day.toLowerCase()}`}
                >
                  <span className="font-medium">{d.day}</span>
                  <span className="text-[#A1A1AA]">{d.range || "—"}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ icon, title, value, sub, testId, action }) => (
  <div
    data-testid={testId}
    className="rounded-xl border border-[#262626] bg-[#171717] p-5 le11-shadow-card"
  >
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A1A1AA]">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A] text-[#C9A875] border border-[#262626]">
        {icon}
      </span>
      {title}
    </div>
    <div className="mt-3 text-lg font-semibold tracking-tight">{value}</div>
    {sub && <div className="text-sm text-[#A1A1AA] mt-0.5">{sub}</div>}
    {action}
  </div>
);

export default Contact;
